/**
 * State-Gated Service-Area Picker
 *
 * Two-step TYPED flow: pick a US state first, then search cities/counties
 * WITHIN that state. Once a state is chosen the search input is enabled and
 * every keystroke fetches matches scoped to the selected state — the state is
 * sent to the API as a separate param, so the user only ever types the bare
 * city or county name (no redundant ", ST" tail in the visible input).
 *
 * The device-location and map pin-drop paths from the previous picker are
 * preserved unchanged — they determine the state themselves (via reverse
 * geocoding) and are therefore NOT gated on the state selector. When any of
 * those paths produces a `GeoResult`, we sync the state selector to that
 * result's state so the typed flow stays consistent with what was just added.
 *
 * Selecting a result fires `onSelect(GeoResult)` and clears the search text,
 * leaving the state selection sticky so a user can add several areas from the
 * same state without re-picking it. Consumers own the badges list; this
 * component only produces one selection at a time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  Loader2,
  LocateFixed,
  Map as MapIcon,
  MapPin,
  Search,
} from 'lucide-react';
import {
  geocodeServiceArea,
  getUSStates,
  searchGeo,
  GeoResult,
  ReverseGeoMatch,
  USState,
} from '@/lib/location-data';
import { isApproximateFix } from '@/lib/geolocation';
import { useDeviceLocation, DeviceLocationFailure } from '@/hooks/useDeviceLocation';
import ServiceAreaMapPicker from '@/components/ui/ServiceAreaMapPicker';
import { cn } from '@/lib/utils';

export interface StateGatedServiceAreaPickerProps {
  onSelect: (result: GeoResult) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  maxSuggestions?: number;
  /** Hide the "use my current location" / map pin-drop controls entirely. */
  enableDeviceLocation?: boolean;
  /**
   * Service-area string to center the map on when the pin-drop is opened
   * without a device fix (usually the user's first existing area). Falls back
   * to a wide continental-US view when absent or unresolvable.
   */
  mapCenterHint?: string;
}

/**
 * Continental-US fallback view. Only used when we have nothing better: no
 * device fix, and no existing service area to centre on.
 */
const US_FALLBACK = { latitude: 39.5, longitude: -98.35 };

/**
 * One message per failure. See ServiceAreaAutocomplete for the original
 * rationale — different messages let the user know which paths are retryable
 * and which are a dead end this screen cannot fix.
 */
const LOCATION_FAILURE_MESSAGES: Record<DeviceLocationFailure, string> = {
  unsupported: 'This device can’t share its location. Type your city or county above instead.',
  denied:
    'Location is turned off for Trade Loop. You can still type your city or county above — or turn location on for Trade Loop in your device settings and reopen this screen.',
  unavailable: 'We couldn’t get a location fix. Type your city or county above, or try again.',
  timeout: 'Getting your location took too long. Type your city or county above, or try again.',
  'lookup-failed': 'We couldn’t look up that location. Type your city or county above, or try again.',
  'no-match':
    'We couldn’t match your position to a US city or county. Type your service area above instead.',
};

/** Failures where trying again could plausibly work. */
const RETRYABLE_FAILURES: DeviceLocationFailure[] = ['unavailable', 'timeout', 'lookup-failed'];

/**
 * Drop `distanceKm` so consumers receive the same shape from the location path
 * as from the typed path — the ranking distance is a UI detail, not part of
 * the service area.
 */
function toGeoResult(match: ReverseGeoMatch): GeoResult {
  const { distanceKm: _distanceKm, ...geo } = match;
  return geo;
}

export default function StateGatedServiceAreaPicker({
  onSelect,
  disabled = false,
  placeholder = 'Search cities or counties…',
  className,
  maxSuggestions = 10,
  enableDeviceLocation = true,
  mapCenterHint,
}: StateGatedServiceAreaPickerProps) {
  const [states, setStates] = useState<USState[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [selectedState, setSelectedState] = useState<string>('');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeoResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getUSStates()
      .then((list) => {
        if (!cancelled) {
          setStates(list);
          setStatesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setStatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedState || !query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await searchGeo(query, maxSuggestions, selectedState);
        if (!cancelled) {
          setSuggestions(results);
          setShowSuggestions(true);
          setHighlightedIndex(-1);
          setIsSearching(false);
        }
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(false);
          setIsSearching(false);
        }
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selectedState, maxSuggestions]);

  const handleStateChange = useCallback((value: string) => {
    setSelectedState(value);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, []);

  /**
   * Single funnel for every path (typed, location, map pin). We sync the state
   * selector to the picked area's state so the typed flow next time doesn't
   * silently disagree with the badge that was just added.
   */
  const handleSelect = useCallback(
    (result: GeoResult) => {
      onSelect(result);
      setQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      if (result.stateCode && result.stateCode !== selectedState) {
        setSelectedState(result.stateCode);
      }
      inputRef.current?.focus();
    },
    [onSelect, selectedState],
  );

  const location = useDeviceLocation();
  const {
    request: requestLocation,
    reset: resetLocation,
    resolveCoordinates,
    openPinAt,
  } = location;

  const handleUseMyLocation = useCallback(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    void requestLocation();
  }, [requestLocation]);

  const handleLocationConfirm = useCallback(
    (result: GeoResult) => {
      resetLocation();
      handleSelect(result);
    },
    [handleSelect, resetLocation],
  );

  const handlePinCommit = useCallback(
    (latitude: number, longitude: number) => {
      void resolveCoordinates(latitude, longitude);
    },
    [resolveCoordinates],
  );

  /**
   * Open the map WITHOUT reading the device. Wanting to drop a pin is not the
   * same as wanting to share your location, so this path never prompts and
   * stays available when permission is denied.
   *
   * Centre, best first: whatever is typed → the caller's hint (the user's
   * existing service area) → a wide view of the US.
   */
  const handlePickOnMap = useCallback(async () => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);

    for (const candidate of [query, mapCenterHint]) {
      if (!candidate) continue;
      const placed = await geocodeServiceArea(candidate);
      if (placed) {
        await openPinAt(placed.latitude, placed.longitude);
        return;
      }
    }
    await openPinAt(US_FALLBACK.latitude, US_FALLBACK.longitude);
  }, [mapCenterHint, openPinAt, query]);

  const locationFailure: DeviceLocationFailure | null = location.isBlocked
    ? 'denied'
    : location.status === 'error'
      ? location.error
      : null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Wrap the whole result in a single span so the parent `flex gap-2` row
  // treats it as ONE flex item — otherwise the fragment's three children
  // (prefix, matched span, suffix) each become their own flex item and the
  // gap paints a visible space through the middle of the word.
  const highlightMatch = (text: string, q: string) => {
    if (!q) return <span>{text}</span>;
    const lower = text.toLowerCase();
    const idx = lower.indexOf(q.toLowerCase());
    if (idx === -1) return <span>{text}</span>;
    return (
      <span>
        {text.slice(0, idx)}
        <span className="font-semibold text-blue-600">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </span>
    );
  };

  const searchDisabled = disabled || !selectedState;

  return (
    <div ref={wrapperRef} className={cn('w-full space-y-3', className)}>
      <div className="space-y-1.5">
        <Label htmlFor="service-area-state" className="text-sm font-medium text-gray-700">
          State
        </Label>
        <Select
          value={selectedState}
          onValueChange={handleStateChange}
          disabled={disabled || statesLoading}
        >
          <SelectTrigger id="service-area-state" className="h-12">
            <SelectValue placeholder={statesLoading ? 'Loading states…' : 'Select a state'} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {states.map((state) => (
              <SelectItem key={state.isoCode} value={state.isoCode}>
                {state.name} ({state.isoCode})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Local relative wrapper so the suggestion dropdown pins to the bottom
          of the search input rather than to the bottom of the whole picker
          (which would put it below the map/location controls). */}
      <div className="relative space-y-1.5">
        <Label htmlFor="service-area-search" className="text-sm font-medium text-gray-700">
          City or county
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="service-area-search"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedState ? placeholder : 'Select a state first to search'}
            disabled={searchDisabled}
            className="pl-10 pr-10 h-12"
            aria-expanded={showSuggestions}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            role="combobox"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={listRef}
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            role="listbox"
          >
            {suggestions.map((suggestion, index) => {
              const isCounty = suggestion.type === 'county';
              return (
                <button
                  key={`${suggestion.type}-${suggestion.name}-${suggestion.stateCode}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={cn(
                    'w-full px-4 py-3 text-left focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors',
                    index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50',
                  )}
                  onClick={() => handleSelect(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-start gap-2">
                    {isCounty ? (
                      <MapIcon className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {highlightMatch(
                          isCounty ? `${suggestion.name} County` : suggestion.name,
                          query,
                        )}
                        <span
                          className={cn(
                            'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
                            isCounty
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 text-gray-600',
                          )}
                        >
                          {isCounty ? 'County' : 'City'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {suggestion.stateName}
                        {!isCounty && suggestion.county ? ` · ${suggestion.county} County` : ''}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {showSuggestions && query.length >= 2 && suggestions.length === 0 && !isSearching && (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-500 text-center">
              No cities or counties found matching “{query}”
            </p>
          </div>
        )}
      </div>

      {/* Device-location + map pin-drop. NOT gated on the state selector —
          these paths determine the state themselves via reverse geocoding
          and back-fill the selector when they succeed (see handleSelect). */}
      {enableDeviceLocation && (
        <div className="space-y-2">
          {/* Two fixed columns so a longer/shorter idle label doesn't push
              the row height around while the map is mounted below (same
              rationale as ServiceAreaAutocomplete). */}
          <div className="grid grid-cols-2 gap-2">
            {location.isSupported && (
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={disabled || location.isBusy || location.isBlocked}
                className={cn(
                  'inline-flex items-center justify-center gap-1.5 text-sm font-medium rounded-lg px-3 py-2 border transition-colors',
                  'whitespace-nowrap min-w-0',
                  'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-50',
                )}
              >
                {location.isBusy ? (
                  <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                ) : (
                  <LocateFixed className="w-4 h-4 shrink-0" />
                )}
                <span className="truncate">
                  {location.status === 'locating'
                    ? 'Locating…'
                    : location.status === 'resolving'
                      ? 'Finding area…'
                      : 'Use my location'}
                </span>
              </button>
            )}

            {/* Dropping a pin is not sharing your location — must stay
                available when geolocation is denied, unavailable, or absent. */}
            <button
              type="button"
              onClick={() => void handlePickOnMap()}
              disabled={disabled || location.isBusy}
              className={cn(
                'inline-flex items-center justify-center gap-1.5 text-sm font-medium rounded-lg px-3 py-2 border transition-colors',
                'whitespace-nowrap min-w-0',
                'border-gray-200 text-gray-700 bg-white hover:bg-gray-50',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                location.isSupported ? '' : 'col-span-2',
              )}
            >
              <MapIcon className="w-4 h-4 shrink-0" />
              <span className="truncate">Pick on a map</span>
            </button>
          </div>

          {locationFailure && (
            <div
              role="status"
              className={cn(
                'rounded-lg border px-3 py-2 text-xs',
                locationFailure === 'denied'
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-gray-200 bg-gray-50 text-gray-600',
              )}
            >
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{LOCATION_FAILURE_MESSAGES[locationFailure]}</span>
              </div>
              {RETRYABLE_FAILURES.includes(locationFailure) && (
                <button
                  type="button"
                  onClick={handleUseMyLocation}
                  className="mt-1.5 ml-5 underline font-medium hover:no-underline"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {/* Gated on COORDINATES, not on status — keeps the map mounted
              across a pin-drag re-resolve so the map doesn't jump back to
              its initial view on every drag (see ServiceAreaAutocomplete
              for the incident this comment refers to). */}
          {location.coordinates && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
              <ServiceAreaMapPicker
                latitude={location.coordinates.latitude}
                longitude={location.coordinates.longitude}
                onCommit={handlePinCommit}
                disabled={disabled}
              />

              {isApproximateFix(location.coordinates.accuracyMeters) && (
                <p className="text-xs text-amber-700">
                  This is an approximate location. Check the pin and drag it if it’s off.
                </p>
              )}

              <div>
                <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
                  Add this service area:
                  {location.status === 'resolving' && (
                    <Loader2 className="w-3 h-3 animate-spin" aria-label="Updating" />
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  {location.result?.match ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleLocationConfirm(toGeoResult(location.result!.match!))}
                        className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {location.result.match.formatted}
                      </button>
                      {location.result.county && (
                        <button
                          type="button"
                          onClick={() => handleLocationConfirm(location.result!.county!)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                        >
                          <MapIcon className="w-3.5 h-3.5" />
                          {location.result.county.formatted}
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">
                      {location.status === 'resolving'
                        ? 'Finding the area for this pin…'
                        : 'No service area for this pin — drag it somewhere else.'}
                    </p>
                  )}
                </div>
              </div>

              {location.result && location.result.alternatives.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Not quite right? Nearby:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {location.result.alternatives.map((alternative) => (
                      <button
                        key={alternative.formatted}
                        type="button"
                        onClick={() => handleLocationConfirm(toGeoResult(alternative))}
                        className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        {alternative.formatted}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={resetLocation}
                className="text-xs text-gray-500 underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { StateGatedServiceAreaPicker };
